import React, { useRef, useState, useEffect } from 'react';
import {
  makeStyles,
  Tooltip,
  Badge,
  IconButton,
  SvgIcon,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Button,
} from '@material-ui/core';
import { ShoppingCart as ShoppingCartIcon, Minus as MinusIcon, Plus as PlusIcon } from 'react-feather';
import { listenEvent, removeListener, emitEvent } from 'utils/emitter';
import { withApi, withNotification } from 'wrappers';
import endPoints from 'endPoints/endPoints';
import { messages } from 'constantes';
import { Alert } from '@material-ui/lab';
import { Image } from 'components';

const useStyles = makeStyles((theme) => ({
  badge: {
    marginTop: 10,
    marginRight: 5,
  },
  popover: {
    width: "50em",
    padding: theme.spacing(2),
    borderRadius: 16,
  },
  table: {
    minWidth: "100%",
  },
  totalRow: {
    fontWeight: 'bold',
  },
  image: {
    borderRadius: 8,
    width: '40px',
    height: '40px',
  },
  paymentButton: {
    marginTop: 16,
    width: "100%",
    padding: "12px 0",
    fontWeight: "bold",
    fontSize: "1rem",
    backgroundColor: "#c43bd4",
    color: "#fff",
    borderRadius: "8px",
    textTransform: "none",
    '&:hover': {
      backgroundColor: "#852a90",
    },
  }
}));

function ShoppingCart({
  doGet,
  doPut,
  doPost,
  appInfo,
  genericException,
}) {
  const classes = useStyles();
  const ref = useRef();
  const [pop, setPop] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [totalCart, setTotalCart] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const handleOpen = () => setPop(true);
  const handleClose = () => setPop(false);

  useEffect(() => {
    const handler = async () => {
      try {
        const resp = await doGet({
          url: endPoints.misCompras.procesos.cart.base,
        });
        const auxCart = [];
        resp.map(item => auxCart.push({
          id: item.id,
          name: `${item.codigo} - ${item.nombre}`,
          qty: item.quantity,
          price: item.precio,
          image: "https://picsum.photos/200/300",
          thumbnail: item.thumbnail
        }));
        setCartItems(auxCart);
        setTotalItems(auxCart.reduce((sum, item) => sum + parseInt(item.qty), 0));
        setTotalCart(auxCart.reduce((sum, item) => sum + parseFloat(item.price) * parseInt(item.qty), 0));
      } catch (e) {
        genericException(e);
      }
    };
    listenEvent("loadCart", handler);

    if (cartItems.length < 1) {
      handler();
    }

    return () => removeListener("loadCart", handler);
    // eslint-disable-next-line
  }, [])

  const subtotal = cartItems.reduce((sum, item) => sum + parseFloat(item.price) * parseInt(item.qty), 0);
  const total = subtotal;

  const modifyQty = async ({ id, qty }, metodo) => {
    try {
      let auxQty = qty;
      if (metodo === "restar") {
        auxQty -= 1;
      } else {
        auxQty += 1;
      }

      await doPut({
        url: `${endPoints.misCompras.procesos.cart.base}/${id}`,
        data: { qty: auxQty },
      });
      appInfo(messages.crud.update);
      emitEvent("loadCart", "excecute");
    } catch (e) {
      genericException(e);
    }
  }

  const handlePaymentButton = async () => {
    try {
      await doPost({
        url: endPoints.misCompras.procesos.purchase.base,
        data: {
          total_items: totalItems,
          total: totalCart,
        },
      });
      emitEvent("loadCart", "excecute");
      emitEvent("loadCompras", "excecute");
    } catch (e) {
      genericException(e);
    }
  };

  return (
    <>
      <Tooltip title="Carrito">
        <Badge
          badgeContent={cartItems.length}
          color="error"
          classes={{ badge: classes.badge }}
        >
          <IconButton color="inherit" onClick={handleOpen} ref={ref}>
            <SvgIcon fontSize="small">
              <ShoppingCartIcon />
            </SvgIcon>
          </IconButton>
        </Badge>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={handleClose}
        open={pop}
      >
        <Typography variant="h6" gutterBottom>
          Carrito
        </Typography>
        {cartItems.length > 0 ? (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Producto</TableCell>
                    <TableCell align="center">Cantidad</TableCell>
                    <TableCell align="right">Precio</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cartItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell valign="middle" style={{ padding: "12px 16px", verticalAlign: "middle" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                          {item.thumbnail ? (
                            <Image
                              url={`${endPoints.general.maestros.images.base}/${item.thumbnail}`}
                              className={classes.image}
                            />
                          ) : (
                              <img
                                src={item.image}
                                alt={item.name}
                                width="40"
                                height="40"
                                style={{ borderRadius: 8 }}
                              />
                            )}
                          <span style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis"
                          }}>
                            {item.name}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell align="center" valign="middle">
                        <Tooltip title="Restar">
                          <IconButton onClick={() => modifyQty(item, "restar")}>
                            <MinusIcon />
                          </IconButton>
                        </Tooltip>
                        {item.qty}
                        <Tooltip title="Sumar">
                          <IconButton onClick={() => modifyQty(item, "sumar")}>
                            <PlusIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                      <TableCell
                        align="right"
                        valign="middle"
                      >
                        {parseFloat(item.price).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Typography variant="body2" align="right" style={{ marginTop: 8 }}>
              Subtotal: {parseFloat(subtotal).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })} <br />
              <strong className={classes.totalRow}>Total: {parseFloat(total).toLocaleString('es-CO', { style: 'currency', currency: 'COP' })}</strong>
            </Typography>
            <Button
              variant="contained"
              className={classes.paymentButton}
              onClick={handlePaymentButton}
            >
              Pagar
            </Button>
          </>
        ) : (
            <Alert severity="info">No hay productos agregados al carrito</Alert>
          )}
      </Popover>
    </>
  );
}

export default withApi(withNotification(ShoppingCart));
